var _ = require('underscore');

/**
 * Loops n-levels, making a copy of obj with each leaf property passing through
 * the callback.
 * @param  {string}   keyType A string of key types, e.g. 'kkn'. Each k is an object/hash, each n is an array
 * @param  {object}   obj     The object whose properties will be converted
 * @param  {Function} cb      The function that will process each leaf node.
 * @return {object}           an array or object depending on keyType
 */
function deeploop(keyType, obj, cb) {
	if (!obj) {
		return obj;
	}

	var processItem;
	if (keyType.length > 1) {
		processItem = function (i) {
			return deeploop(keyType.substring(1), i, cb);
		};
	} else {
		processItem = cb;
	}

	if (keyType.charAt(0) === 'k') {
		var retObj = {};
		_.each(obj, function (v, k) {
			retObj[k] = processItem(v);
		});
		return retObj;
	} else {
		// (keyType.charAt(0) === 'n')
		var retArray = [];
		_.each(obj, function (v) {
			retArray.push(processItem(v));
		});
		return retArray;
	}
}

function Mapper(map, opts) {
	var self = this;
	self.map = {};
	self.opts = opts || {};

	if (self.opts.methods) {
		_.each(self.opts.methods, function (method, name) {
			self[name] = method;
		});
	}

	var subMapBlackList = {};
	var subMaps = {};
	var leadingKeyRegex = /^(\w*)((?:\[k\]|\[n\])+)?(?:\.(.*))?$/;

	_.each(map, function (clientKey, serverKey) {
		var serverKeyMatch = serverKey.match(leadingKeyRegex);
		var clientKeyMatch = clientKey.match(leadingKeyRegex);

		if (serverKeyMatch[2] !== clientKeyMatch[2]) {
			throw new Error('Subscript mismatch between client and server keys ' + clientKey + ', ' + serverKey);
		}

		if (!!serverKeyMatch[1] !== !!clientKeyMatch[1]) {
			throw new Error('Client key and server key must both be blank or neither be blank');
		}

		if (!serverKeyMatch[3]) {
			// In the case of s[k][n]:someProp[k][n]. We ignore [k] or [n] subscripts
			// since there are no properties inside the array or object values to translate
			// the we will doublecheck that things are right

			self.map[serverKey.replace(/\[.*?\]/g, '')] = clientKey.replace(/\[.*?\]/g, '');
		} else {
			if (!serverKeyMatch) throw new Error('Invalid key ' + serverKey);
			if (!clientKeyMatch) throw new Error('Invalid key ' + clientKey);

			if (clientKey.split('.').length !== serverKey.split('.').length) {
				throw new Error('Depth mismatch between ' + clientKey + ' and ' + serverKey);
			}

			var serverBaseKey = serverKeyMatch[1] + (serverKeyMatch[2] || '');
			var clientBaseKey = clientKeyMatch[1] + (clientKeyMatch[2] || '');

			if (subMapBlackList[serverBaseKey] || subMapBlackList[clientBaseKey]) {
				throw new Error('You cannot map the same property as both an array and an object');
			}

			if (serverKeyMatch[2]) {
				var altSubscript = serverKeyMatch[2] === '[k]' ? '[n]' : '[k]';
				subMapBlackList[serverBaseKey + altSubscript] = 1;
				subMapBlackList[clientBaseKey + altSubscript] = 1;
			}

			// var subMap = subMaps[baseKey] = subMaps[baseKey] || {};
			if (!subMaps[serverKeyMatch[1]]) {
				subMaps[serverKeyMatch[1]] = {
					clientKey: clientKeyMatch[1],
					keys: {},
					clientSubscript: clientKeyMatch[2]
				};
			}
			var subMap = subMaps[serverKeyMatch[1]].keys;

			subMap[serverKeyMatch[3]] = clientKeyMatch[3];
		}
	});

	_.each(subMaps, function (subMap, topServerKey) {
		self.map[topServerKey] = new Mapper(subMap.keys, {
			clientKey: subMap.clientKey,

			// store subscript as letters only, so instead of "[k][n]" we store "kn"
			clientSubscript: (subMap.clientSubscript || '').replace(/[^nk]/g, '')
		});
	});
}

Mapper.prototype.serverToClient = function (d) {
	var self = this;

	if (!d) {
		return d;
	}

	var d2 = {};
	_.each(this.map, function (clientKey, serverKey) {
		// Changing an account requires only changed
		// properties be included. So don't set values that are undefined
		// on the new object this way the object can neatly be sparse,
		// missing some properties.
		if (typeof d[serverKey] !== 'undefined') {
			if (typeof clientKey === 'string') {
				d2[clientKey] = d[serverKey];
			} else if (clientKey.opts.clientSubscript) {
				d2[clientKey.opts.clientKey] = deeploop(clientKey.opts.clientSubscript, d[serverKey], _.bind(clientKey.serverToClient, clientKey));
			} else {
				// handle normal submap
				var serverVal = d[serverKey];
				d2[clientKey.opts.clientKey] = clientKey.serverToClient(serverVal);
			}
		}
	});

	// handle case of '[k].s':'[k].someProperty'
	if (this.map[''] && this.map[''].opts.clientSubscript.charAt(0) === 'k') {
		var dHash = {};
		Object.keys(d).forEach(function (k) {
			if (!self.map.hasOwnProperty(k) && typeof d[k] === 'object') {
				dHash[k] = d[k];
			}
		});

		_.extend(d2, deeploop(self.map[''].opts.clientSubscript, dHash, _.bind(self.map[''].serverToClient, self.map[''])));
	} else if (this.map[''] && this.map[''].opts.clientSubscript.charAt(0) === 'n' && Array.isArray(d)) {
		d2 = deeploop(self.map[''].opts.clientSubscript, d, _.bind(self.map[''].serverToClient, self.map['']));
	}

	if (this.opts.postServerToClient) {
		this.opts.postServerToClient(d, d2);
	}
	return d2;
};
/**
 * If the passed object is an error (status is present and is not 200), then
 * turns the server error structure into a format more convenient to quickly
 * check for specific errors in the UI. e.g. {"i":["f"], "r":["e"]} will be
 * changed into {"firstName":{"invalid":true}, "email":{"required":true}}
 * The status and code properties of the server error are not altered.
 *
 * If the object passed is not an error (missing status or ==200) then the
 * object is returned unaltered;
 *
 * @param  {object} err the error from the server
 * @return {object}     A new object that can be referenced with o[fieldName][errorType]
 */
/* eslint-disable consistent-return */
Mapper.prototype.serverErrorToClient = function (err) {
	var self = this;

	if (!err) {
		return;
	}

	if (!err.status || err.status === 200) {
		return err;
	}

	var errTypes = {
		i: 'invalid',
		r: 'required',
		n: 'notAllowed'
	};

	var errorData = null;
	if (err.response && err.response.body && err.response.body.data) {
		errorData = err.response.body.data;
	}

	var errorCode = err.code;
	if (!errorCode && err.response && err.response.body && err.response.body.code) {
		errorCode = err.response.body.code;
	}

	var err2 = {
		status: err.status,
		conflict: err.conflict,
		code: errorCode,
		data: errorData
	};

	_.map(errTypes, function (clientErrKey, serverErrKey) {
		var errList = err[serverErrKey] || [];
		_.each(errList, function (e) {
			var clientKey = self.map[e];

			if (!err2[clientKey]) {
				err2[clientKey] = {};
			}
			err2[clientKey][clientErrKey] = true;
		});
	});

	if (this.opts.postServerErrorToClient) {
		this.opts.postServerErrorToClient(err, err2);
	}

	return err2;
};
/* eslint-enable consistent-return */

Mapper.prototype.clientToServer = function (d) {
	var self = this;
	var d2 = {};
	var clientKeys = {};

	_.each(this.map, function (clientKey, serverKey) {
		clientKeys[clientKey] = true;

		// Changing an account requires only changed
		// properties be included. So don't set values that are undefined
		// on the new object this way the object can neatly be sparse,
		// missing some properties.
		if (typeof clientKey === 'string') {
			if (typeof d[clientKey] !== 'undefined') {
				d2[serverKey] = d[clientKey];
			}
		} else if (clientKey.opts.clientSubscript && d[clientKey.opts.clientKey]) {
			d2[serverKey] = deeploop(clientKey.opts.clientSubscript, d[clientKey.opts.clientKey], _.bind(clientKey.clientToServer, clientKey));
		} else if (clientKey.opts.clientKey && d[clientKey.opts.clientKey]) {
			// handle normal submap
			var clientVal = d[clientKey.opts.clientKey];
			d2[serverKey] = clientKey.clientToServer(clientVal);
		}
	});

	// handle case of '[k].s':'[k].someProperty'
	if (this.map[''] && this.map[''].opts.clientSubscript.charAt(0) === 'k') {
		var dHash = {};
		Object.keys(d).forEach(function (k) {
			if (!clientKeys[k] && typeof d[k] === 'object') {
				dHash[k] = d[k];
			}
		});

		_.extend(d2, deeploop(self.map[''].opts.clientSubscript, dHash, _.bind(self.map[''].clientToServer, self.map[''])));
	} else if (this.map[''] && this.map[''].opts.clientSubscript.charAt(0) === 'n' && Array.isArray(d)) {
		d2 = deeploop(self.map[''].opts.clientSubscript, d, _.bind(self.map[''].clientToServer, self.map['']));
	}

	if (this.opts.postClientToServer) {
		this.opts.postClientToServer(d, d2);
	}

	return d2;
};

Mapper.prototype.clientProperties = function () {
	var props = [];
	_.each(this.map, function (clientKey) {
		props.push(clientKey);
	});
	return props;
};

Mapper.prototype.serverProperties = function () {
	var props = [];
	_.each(this.map, function (clientKey, serverKey) {
		props.push(serverKey);
	});
	return props;
};

Mapper.deeploop = deeploop;

module.exports = Mapper;
